import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

interface Win {
  id: string;
  timeframe: string;
  title: string;
  progress: number;
  currentValue: number | null;
  targetValue: number | null;
}

interface WinsScore {
  totalPoints: number;
  currentStreak: number;
  level: number;
}

const LEVEL_NAMES = ['Novice', 'Apprentice', 'Achiever', 'Expert', 'Master'];
const TIMEFRAME_COLORS: Record<string, string> = {
  QUICK: 'bg-orange-500',
  DAILY: 'bg-blue-500',
  WEEKLY: 'bg-purple-500',
  MONTHLY: 'bg-amber-500',
};

interface Props {
  mundusId: string;
}

export default function WinsSummaryCard({ mundusId }: Props) {
  const navigate = useNavigate();
  const [activeWins, setActiveWins] = useState<Win[]>([]);
  const [score, setScore] = useState<WinsScore | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWins();
  }, [mundusId]);

  const loadWins = async () => {
    try {
      const res = await api.get(`/api/mundus/${mundusId}/wins`);
      setActiveWins(res.data.activeWins || []);
      setScore(res.data.score || null);
    } catch {
      // No wins yet
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-24 mb-3" />
        <div className="h-3 bg-slate-100 rounded w-full mb-2" />
        <div className="h-3 bg-slate-100 rounded w-3/4" />
      </div>
    );
  }

  const levelName = LEVEL_NAMES[Math.min((score?.level || 1) - 1, LEVEL_NAMES.length - 1)];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Score header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">{score?.totalPoints || 0} pts</div>
            <div className="text-xs text-blue-100">{levelName}</div>
          </div>
          {(score?.currentStreak || 0) > 0 && (
            <div className="text-right">
              <div className="text-lg font-bold">{score!.currentStreak}</div>
              <div className="text-xs text-blue-100">day streak</div>
            </div>
          )}
        </div>
      </div>

      {/* Active wins (top 2) */}
      <div className="p-4">
        {activeWins.length > 0 ? (
          <div className="space-y-3">
            {activeWins.slice(0, 2).map((win) => (
              <div key={win.id}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-700 font-medium truncate">{win.title}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    {win.currentValue || 0}/{win.targetValue || 1}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${TIMEFRAME_COLORS[win.timeframe] || 'bg-blue-500'}`}
                    style={{ width: `${Math.min(win.progress, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No active wins yet. Start chatting to earn wins!</p>
        )}

        <button
          onClick={() => navigate(`/mundus/${mundusId}`)}
          className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          See all wins →
        </button>
      </div>
    </div>
  );
}
